import React from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, CreditCard, Lock, ShieldCheck, ShieldHalf } from "lucide-react";
import { TASK } from "@/constants/testIds";

export default function TaskActionBar({ task, role, busy, onFundEscrow, onStripePay, onStatusChange }) {
  const isClient = role === "client";
  const isAgent = role === "agent";

  const serviceFeeUnpaid = task.service_fee_status === "unpaid";
  const itemCostUnpaid = task.item_cost_status === "unpaid";
  const hasItemCost = Number(task.budget_ngn || 0) > 0;
  const total = Number(task.service_fee_ngn || 0) + Number(task.budget_ngn || 0);
  const fmt = (n) => `₦${Number(n || 0).toLocaleString()}`;

  // Bundle one button when BOTH are unpaid AND there's an item cost
  const showBundle = isClient && serviceFeeUnpaid && itemCostUnpaid && hasItemCost;

  const showFundItem =
    isClient &&
    !showBundle &&
    task.service_fee_status !== "unpaid" &&
    ["assigned", "in_progress"].includes(task.status) &&
    hasItemCost &&
    itemCostUnpaid;

  // Pay2Hold is available for service-only tasks (no goods purchase) — placeholder for now
  const isServiceOnly = task.pay2hold_eligible === true
    || (task.purchase_type && task.purchase_type !== "buy_items")
    || !hasItemCost;
  const showPay2Hold = isClient && serviceFeeUnpaid && isServiceOnly;

  return (
    <div className="eb-card p-5 flex flex-wrap items-center gap-3">
      {showBundle && (
        <>
          <button
            data-testid={TASK.fundBundleBtn}
            onClick={() => onFundEscrow("bundle")}
            disabled={busy}
            className="eb-btn eb-btn-primary"
          >
            <Lock className="w-4 h-4" /> Fund task ({fmt(total)})
          </button>
          <button
            data-testid={TASK.stripeBundleBtn}
            onClick={() => onStripePay("bundle")}
            disabled={busy}
            className="eb-btn eb-btn-ghost"
          >
            <CreditCard className="w-4 h-4" /> Pay via Stripe (USD)
          </button>
          <span className="text-xs text-[var(--eb-text-dim)]">
            = service fee {fmt(task.service_fee_ngn)} + item {fmt(task.budget_ngn)}
          </span>
        </>
      )}

      {isClient && serviceFeeUnpaid && !showBundle && (
        <>
          <button
            data-testid={TASK.funEscrowBtn}
            onClick={() => onFundEscrow("service_fee")}
            disabled={busy}
            className="eb-btn eb-btn-primary"
          >
            <Lock className="w-4 h-4" /> Fund service fee ({fmt(task.service_fee_ngn)})
          </button>
          <button
            data-testid={TASK.stripeBtn}
            onClick={() => onStripePay("service_fee")}
            disabled={busy}
            className="eb-btn eb-btn-ghost"
          >
            <CreditCard className="w-4 h-4" /> Pay via Stripe (USD)
          </button>
        </>
      )}

      {showPay2Hold && (
        <button
          type="button"
          data-testid={TASK.pay2holdBtn}
          onClick={() => toast.info("Pay2Hold launches soon — your task escrow option for project-only payments.")}
          disabled={busy}
          className="eb-btn eb-btn-ghost relative"
          title="Pay2Hold — coming soon"
        >
          <ShieldHalf className="w-4 h-4" /> Pay2Hold
          <span className="ml-1 text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full bg-[var(--eb-secondary)]/15 text-[var(--eb-secondary)] font-[700]">
            Soon
          </span>
        </button>
      )}

      {showFundItem && (
        <button
          data-testid={TASK.funItemBtn}
          onClick={() => onFundEscrow("item_cost")}
          disabled={busy}
          className="eb-btn eb-btn-primary"
        >
          <Lock className="w-4 h-4" /> Fund item cost ({fmt(task.budget_ngn)})
        </button>
      )}

      {isAgent && task.status === "assigned" && (
        <button
          data-testid={TASK.startBtn}
          onClick={() => onStatusChange("in_progress")}
          disabled={busy}
          className="eb-btn eb-btn-primary"
        >
          <ArrowRight className="w-4 h-4" /> Start task
        </button>
      )}

      {isAgent && task.status === "in_progress" && (
        <button
          data-testid={TASK.finishBtn}
          onClick={() => onStatusChange("awaiting_approval")}
          disabled={busy}
          className="eb-btn eb-btn-primary"
        >
          <CheckCircle2 className="w-4 h-4" /> Mark task complete
        </button>
      )}

      {isAgent && task.status === "awaiting_approval" && (
        <span className="inline-flex items-center gap-2 text-sm text-[var(--eb-success)] px-3 py-2 rounded-lg bg-[var(--eb-success)]/10 border border-[var(--eb-success)]/30">
          <CheckCircle2 className="w-4 h-4" /> Submitted — waiting for client approval
        </span>
      )}

      {isClient && task.status === "awaiting_approval" && (
        <button
          data-testid={TASK.approveBtn}
          onClick={() => onStatusChange("completed")}
          disabled={busy}
          className="eb-btn eb-btn-primary"
        >
          <CheckCircle2 className="w-4 h-4" /> Approve & release escrow
        </button>
      )}

      {task.service_fee_status === "held_escrow" && task.item_cost_status === "held_escrow" && (
        <span className="inline-flex items-center gap-1.5 text-xs text-[var(--eb-success)]">
          <ShieldCheck className="w-4 h-4" /> Total {fmt(total)} in escrow
        </span>
      )}
      {task.service_fee_status === "held_escrow" && task.item_cost_status !== "held_escrow" && (
        <span className="inline-flex items-center gap-1.5 text-xs text-[var(--eb-success)]">
          <ShieldCheck className="w-4 h-4" /> Service fee in escrow
        </span>
      )}
      {task.service_fee_status === "released" && (
        <span className="inline-flex items-center gap-1.5 text-xs text-[var(--eb-success)]">
          <CheckCircle2 className="w-4 h-4" /> Funds released
        </span>
      )}
    </div>
  );
}
